import type { PaginationEventHandlersParams } from '@ksyos/design-system/dist/components/pagination/pagination';
import type { FundusScreeningThirdParty } from '@common/fundusScreening/thirdParties';
import type {
    FundusScreeningGetManualImportLogsApiResponse,
    FundusScreeningManualImportApiResponse,
    FundusScreeningManualImportLogsSortByProperties,
} from '@common/interfaces/api/fundusScreeningApi';
import type { SortingOptions } from '@frontend/utils/hooks/useSorting';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { fetchJson, uploadFile } from '@frontend/store/fetch';

export async function fundusScreeningManualImportApi(
    thirdParty: FundusScreeningThirdParty,
    file: File,
    _signal: AbortSignal
) {
    const formData = new FormData();

    formData.append('thirdParty', thirdParty);

    const response = await uploadFile<{ result: FundusScreeningManualImportApiResponse }>(
        '/api/fundusScreening/manualImport',
        file,
        // 10 minutes timeout because this request is long running
        AbortSignal.timeout(10 * 60 * 1000),
        formData
    );

    const result = nonNullable(response.json).result;

    if (result.zipFile) {
        downloadZipFile(result.zipFile);
    }

    return result;
}

export async function getFundusScreeningManualImportLogsApi(
    pagination: PaginationEventHandlersParams,
    sorting: SortingOptions<FundusScreeningManualImportLogsSortByProperties>,
    abortSignal?: AbortSignal
): Promise<FundusScreeningGetManualImportLogsApiResponse> {
    const url = new UrlBuilder('/api/fundusScreening/manualImport/logs').add(pagination).add(sorting).build();

    const response = await fetchJson<FundusScreeningGetManualImportLogsApiResponse>({
        method: 'GET',
        signal: abortSignal,
        url,
    });

    return { logs: response.json?.logs ?? [], totalItems: response.json?.totalItems ?? 0 };
}

function downloadZipFile(zipFile: NonNullable<FundusScreeningManualImportApiResponse['zipFile']>) {
    // @ts-expect-error for some reason the type is not correct of the Buffer but it only works with `zipFile.buffer.data`
    const blob = new Blob([new Uint8Array(zipFile.buffer.data)]);

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.setAttribute('download', zipFile.fileName);

    a.click();
    a.remove();

    URL.revokeObjectURL(url);
}
